import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getFinancialPostingCashierId,
   getFinancialPostingDate,
   getFinancialPostingRoom,
   getFinancialPostingSource,
   getFinancialPostingTime,
   getFinancialPostingTransactionCode,
   getFinancialPostingTransactionDescription,
   getFinancialPostingTransactionDisplay,
   getFinancialPostingUserDisplay
} from '../utils/financial-posting.utils';
import {
   getFirstBoolean,
   getFirstNumber,
   getFirstString
} from '../utils/report-field.utils';
import {
   formatCurrency,
   formatFlexibleDate,
   formatTime
} from '../utils/report-formatting.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const TA_COMMS_TRANSACTION_DESCRIPTION_PATHS = [
   'TransactionCodeDescription',
   'transactionCodeDescription',
   'TransactionDescription',
   'transactionDescription',
   'Description',
   'description'
] as const;

const LAYOUT_SUMMARY = 'summary';
const LAYOUT_FULL_DETAILS = 'full-details';

type TaCommsRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   roomNumber: string;
   name: string;
   confirmationNumber: string;
   reservationId: string;
   company: string;
   transaction: string;
   transactionCode: string;
   transactionDescription: string;
   folioWindowNo: string;
   amount: string;
   amountRaw: number;
   roomRevenue: string;
   roomRevenueRaw: number | null;
   commissionPercent: string;
   commissionPercentRaw: number | null;
   user: string;
   cashierId: string;
   reservationEnrichmentResolved: boolean | null;
   reservationEnrichmentStatus: string;
   reservationEnrichmentMessage: string;
   hasRealTyallsRowId: boolean;
};

function getRowId(row: unknown): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      getFirstString(row, ['Id', 'id', 'ReservationId', 'reservationId']) ||
      `ta-comms:${getFinancialPostingDate(
         row
      )}:${getFinancialPostingRoom(row)}:${getFirstString(row, [
         'ConfirmationNumber',
         'confirmationNumber'
      ])}:${getFinancialPostingTransactionCode(row)}`
   );
}

function getAmount(row: unknown): number {
   const credit = getFirstNumber(row, ['Credit', 'credit']);

   if (credit !== null) {
      return credit;
   }

   const debit = getFirstNumber(row, ['Debit', 'debit']);

   if (debit !== null) {
      return debit;
   }

   const amount = getFirstNumber(row, ['Amount', 'amount', 'AmountRaw', 'amountRaw']);

   return amount ?? 0;
}

function formatOptionalCurrency(value: number | null): string {
   return value === null ? '' : formatCurrency(value);
}

function formatCommissionPercent(value: number | null): string {
   return value === null ? '' : `${value.toFixed(2)}%`;
}

function getResolvedDisplay(value: boolean | null): string {
   if (value === true) {
      return 'Resolved';
   }

   if (value === false) {
      return 'Not Resolved';
   }

   return 'Unknown';
}

function hasBackendRisk(row: unknown): boolean {
   const sourceRow = row as any;

   return (
      sourceRow?.HasRisk === true ||
      (Array.isArray(sourceRow?.Risks) && sourceRow.Risks.length > 0)
   );
}

function normaliseRows(reportJson: unknown, backendRiskOnly = false): TaCommsRow[] {
   const { rows: sourceRows } = getFinancialPostingSource(reportJson, [
      'TaComms',
      'TAComms'
   ]);

   const selectedRows = backendRiskOnly
      ? sourceRows.filter((row) => hasBackendRisk(row))
      : sourceRows;

   return selectedRows
      .map((row) => {
         const dateRaw = getFinancialPostingDate(row);
         const amountRaw = getAmount(row);

         const roomRevenueRaw = getFirstNumber(row, ['RoomRevenue', 'roomRevenue']);

         const commissionPercentRaw = getFirstNumber(row, [
            'CommissionPercent',
            'commissionPercent'
         ]);

         const reservationEnrichmentResolved = getFirstBoolean(row, [
            'ReservationEnrichmentResolved',
            'reservationEnrichmentResolved'
         ]);

         return {
            rowId: getRowId(row),
            date: formatFlexibleDate(dateRaw),
            dateRaw,
            time: formatTime(getFinancialPostingTime(row)),
            roomNumber: getFinancialPostingRoom(row),
            name: getFirstString(row, ['Name', 'name', 'GuestName', 'guestName']),
            confirmationNumber: getFirstString(row, [
               'ConfirmationNumber',
               'confirmationNumber'
            ]),
            reservationId: getFirstString(row, ['ReservationId', 'reservationId']),
            company: getFirstString(row, ['Company', 'company']),
            transaction: getFinancialPostingTransactionDisplay(
               row,
               TA_COMMS_TRANSACTION_DESCRIPTION_PATHS
            ),
            transactionCode: getFinancialPostingTransactionCode(row),
            transactionDescription: getFinancialPostingTransactionDescription(
               row,
               TA_COMMS_TRANSACTION_DESCRIPTION_PATHS
            ),
            folioWindowNo: getFirstString(row, ['FolioWindowNo', 'folioWindowNo']),
            amount: formatCurrency(amountRaw),
            amountRaw,
            roomRevenue: formatOptionalCurrency(roomRevenueRaw),
            roomRevenueRaw,
            commissionPercent: formatCommissionPercent(commissionPercentRaw),
            commissionPercentRaw,
            user: getFinancialPostingUserDisplay(row),
            cashierId: getFinancialPostingCashierId(row),
            reservationEnrichmentResolved,
            reservationEnrichmentStatus: getResolvedDisplay(
               reservationEnrichmentResolved
            ),
            reservationEnrichmentMessage: getFirstString(row, [
               'ReservationEnrichmentMessage',
               'reservationEnrichmentMessage'
            ]),
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter(
         (row) =>
            row.roomNumber ||
            row.name ||
            row.confirmationNumber ||
            row.amountRaw !== 0
      );
}

function sortRows(rows: TaCommsRow[]): TaCommsRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);

      if (dateCompare !== 0) {
         return dateCompare;
      }

      const aRoom = Number(a.roomNumber);
      const bRoom = Number(b.roomNumber);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

function flattenTaCommsRows(
   reportJson: unknown,
   backendRiskOnly: boolean
): ReportRowVm[] {
   const rows = sortRows(normaliseRows(reportJson, backendRiskOnly));

   return rows.map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'taComms',
         date: row.date,
         dateRaw: row.dateRaw,
         time: row.time,
         roomNumber: row.roomNumber,
         name: row.name,
         confirmationNumber: row.confirmationNumber,
         reservationId: row.reservationId,
         company: row.company,
         transaction: row.transaction,
         transactionCode: row.transactionCode,
         transactionDescription: row.transactionDescription,
         folioWindowNo: row.folioWindowNo,
         amount: row.amount,
         amountRaw: row.amountRaw,
         roomRevenue: row.roomRevenue,
         roomRevenueRaw: row.roomRevenueRaw,
         commissionPercent: row.commissionPercent,
         commissionPercentRaw: row.commissionPercentRaw,
         user: row.user,
         cashierId: row.cashierId,
         reservationEnrichmentResolved: row.reservationEnrichmentResolved,
         reservationEnrichmentStatus: row.reservationEnrichmentStatus,
         reservationEnrichmentMessage: row.reservationEnrichmentMessage
      }
   }));
}

function flattenFullDetails(reportJson: unknown): ReportRowVm[] {
   return flattenTaCommsRows(reportJson, false);
}

function flattenRiskRows(reportJson: unknown): ReportRowVm[] {
   return flattenTaCommsRows(reportJson, true);
}

const TA_COMMS_COLUMNS: ReportDefinition['columns'] = [
   {
      key: 'date',
      header: 'TRN DATE',
      width: '105px',
      sort: {
         type: 'date',
         getValue: (row) => row.cells['dateRaw']
      }
   },
   {
      key: 'roomNumber',
      header: 'ROOM',
      width: '90px',
      sort: { type: 'string' }
   },
   {
      key: 'name',
      header: 'GUEST NAME',
      width: '190px',
      maxChars: 80,
      sort: { type: 'string' }
   },
   {
      key: 'company',
      header: 'COMPANY',
      width: '190px',
      maxChars: 80,
      sort: { type: 'string' }
   },
   {
      key: 'transaction',
      header: 'TRANSACTION',
      width: '300px',
      maxChars: 100,
      sort: { type: 'string' }
   },
   {
      key: 'amount',
      header: 'AMOUNT',
      width: '120px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['amountRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'roomRevenue',
      header: 'ROOM REVENUE',
      width: '135px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['roomRevenueRaw']
      }
   },
   {
      key: 'commissionPercent',
      header: 'COMMISSION %',
      width: '135px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['commissionPercentRaw']
      }
   },
   {
      key: 'user',
      header: 'USER',
      width: '160px',
      sort: { type: 'string' }
   }
];

export const TA_COMMS_DEFINITION: ReportDefinition = {
   reportType: 'TaComms',
   version: 7,
   title: 'TA Comms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'TA Comms'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['taComms']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['taComms'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   rowDetails: {
      enabled: true,
      selectableRowTypes: ['taComms'],
      fields: [
         {
            key: 'cashierId',
            label: 'Cashier ID'
         },
         {
            key: 'reservationId',
            label: 'Reservation ID'
         },
         {
            key: 'confirmationNumber',
            label: 'Confirmation ID'
         },
         {
            key: 'folioWindowNo',
            label: 'Folio'
         }
      ]
   },

   columns: TA_COMMS_COLUMNS,

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'roomNumber', label: 'Room', type: 'string' },
         { key: 'name', label: 'Guest Name', type: 'string' },
         { key: 'company', label: 'Company', type: 'string' },
         {
            key: 'confirmationNumber',
            label: 'Confirmation Number',
            type: 'string'
         },
         {
            key: 'transaction',
            label: 'Transaction',
            type: 'string'
         },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         {
            key: 'roomRevenue',
            label: 'Room Revenue',
            type: 'number',
            getValue: (row) => row.cells['roomRevenueRaw']
         },
         {
            key: 'commissionPercent',
            label: 'Commission %',
            type: 'number',
            getValue: (row) => row.cells['commissionPercentRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: flattenFullDetails,

   layouts: [
      {
         id: LAYOUT_SUMMARY,
         label: 'Summary',
         columns: TA_COMMS_COLUMNS,
         flattenRows: flattenRiskRows
      },
      {
         id: LAYOUT_FULL_DETAILS,
         label: 'Full Details',
         columns: TA_COMMS_COLUMNS,
         flattenRows: flattenFullDetails
      }
   ]
};
